import React, { useMemo, useState } from 'react';
import { Building2, Check, Loader2, Mail, Send } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Textarea } from '@/shared/ui/textarea';
import { formatUnitLabel, type UserMinimal } from '../types';
import type { UnitTourReportUnit } from './UnitTourReportDialog';

interface UnitReportShareDialogProps {
  open: boolean;
  units: UnitTourReportUnit[];
  users: UserMinimal[];
  sharing: boolean;
  onOpenChange: (open: boolean) => void;
  onShare: (
    units: UnitTourReportUnit[],
    recipientEmails: string[],
    message: string,
  ) => Promise<void> | void;
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseManualEmails = (value: string) =>
  value
    .split(/[\s,;]+/)
    .map((email) => email.trim().toLowerCase())
    .filter((email) => emailPattern.test(email));

const displayUserName = (user: UserMinimal) => {
  const name = `${user.first_name} ${user.last_name}`.trim();
  return name || user.email;
};

export const UnitReportShareDialog: React.FC<UnitReportShareDialogProps> = ({
  open,
  units,
  users,
  sharing,
  onOpenChange,
  onShare,
}) => {
  const [unitQuery, setUnitQuery] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [selectedUnitIds, setSelectedUnitIds] = useState<Set<number>>(new Set());
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [manualEmails, setManualEmails] = useState('');
  const [message, setMessage] = useState('');

  const selectableUsers = useMemo(
    () => users.filter((user) => !!user.email).sort((a, b) => displayUserName(a).localeCompare(displayUserName(b))),
    [users],
  );

  const filteredUnits = useMemo(() => {
    const normalized = unitQuery.trim().toLowerCase();
    if (!normalized) return units;
    return units.filter((unit) => formatUnitLabel(unit).toLowerCase().includes(normalized));
  }, [unitQuery, units]);

  const filteredUsers = useMemo(() => {
    const normalized = userQuery.trim().toLowerCase();
    if (!normalized) return selectableUsers;
    return selectableUsers.filter((user) =>
      `${displayUserName(user)} ${user.email}`.toLowerCase().includes(normalized),
    );
  }, [selectableUsers, userQuery]);

  const selectedUnits = useMemo(
    () => units.filter((unit) => selectedUnitIds.has(unit.id)),
    [selectedUnitIds, units],
  );

  const recipientEmails = useMemo(() => {
    const fromUsers = selectableUsers
      .filter((user) => selectedUserIds.has(user.id))
      .map((user) => user.email.trim().toLowerCase());
    return Array.from(new Set([...fromUsers, ...parseManualEmails(manualEmails)]));
  }, [manualEmails, selectableUsers, selectedUserIds]);

  const visibleUnitIds = useMemo(() => filteredUnits.map((unit) => unit.id), [filteredUnits]);
  const allVisibleUnitsSelected =
    visibleUnitIds.length > 0 && visibleUnitIds.every((id) => selectedUnitIds.has(id));
  const someVisibleUnitsSelected =
    visibleUnitIds.some((id) => selectedUnitIds.has(id)) && !allVisibleUnitsSelected;

  const toggleUnit = (unitId: number) => {
    setSelectedUnitIds((current) => {
      const next = new Set(current);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });
  };

  const toggleVisibleUnits = () => {
    setSelectedUnitIds((current) => {
      const next = new Set(current);
      if (allVisibleUnitsSelected) {
        visibleUnitIds.forEach((id) => next.delete(id));
      } else {
        visibleUnitIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleUser = (userId: number) => {
    setSelectedUserIds((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const reset = () => {
    setSelectedUnitIds(new Set());
    setSelectedUserIds(new Set());
    setManualEmails('');
    setMessage('');
    setUnitQuery('');
    setUserQuery('');
  };

  const handleShare = async () => {
    if (selectedUnits.length === 0 || recipientEmails.length === 0) return;
    await onShare(selectedUnits, recipientEmails, message.trim());
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[860px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" strokeWidth={1.9} />
            Share by Unit/Area
          </DialogTitle>
          <DialogDescription>
            Choose one or more units or areas and recipients. Each selection is sent as its own punch list report.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="flex cursor-pointer items-center gap-2 rounded-[11px] border border-border bg-card px-3.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent">
                <Checkbox
                  checked={
                    allVisibleUnitsSelected ? true : someVisibleUnitsSelected ? 'indeterminate' : false
                  }
                  disabled={filteredUnits.length === 0}
                  onCheckedChange={toggleVisibleUnits}
                  aria-label="Select all visible units or areas"
                />
                Select all visible
              </label>
              <Badge variant="secondary">{selectedUnits.length} units/areas</Badge>
            </div>

            <Command shouldFilter={false} className="rounded-[13px] border border-border bg-card">
              <CommandInput
                value={unitQuery}
                onValueChange={setUnitQuery}
                placeholder="Search units, areas, or layouts..."
              />
              <CommandList className="max-h-[340px]">
                <CommandEmpty>No units or areas found.</CommandEmpty>
                <CommandGroup heading="Units/Areas">
                  {filteredUnits.map((unit) => {
                    const selected = selectedUnitIds.has(unit.id);
                    return (
                      <CommandItem
                        key={unit.id}
                        value={`${unit.id}-${formatUnitLabel(unit)}`}
                        onSelect={() => toggleUnit(unit.id)}
                        className="cursor-pointer"
                      >
                        <Checkbox
                          checked={selected}
                          aria-label={`Select ${formatUnitLabel(unit)}`}
                          className="pointer-events-none"
                        />
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{formatUnitLabel(unit)}</p>
                          {unit.layout_name && (
                            <p className="truncate text-xs text-muted-foreground">{unit.name}</p>
                          )}
                        </div>
                        {selected && <Check className="h-4 w-4 text-primary" />}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-medium text-foreground">Recipients</div>
              <Badge variant="secondary">{recipientEmails.length} emails</Badge>
            </div>

            <Command shouldFilter={false} className="rounded-[13px] border border-border bg-card">
              <CommandInput
                value={userQuery}
                onValueChange={setUserQuery}
                placeholder="Search project users..."
              />
              <CommandList className="max-h-[190px]">
                <CommandEmpty>No users found.</CommandEmpty>
                <CommandGroup heading="Project users">
                  {filteredUsers.map((user) => {
                    const selected = selectedUserIds.has(user.id);
                    return (
                      <CommandItem
                        key={user.id}
                        value={`${user.id}-${displayUserName(user)}-${user.email}`}
                        onSelect={() => toggleUser(user.id)}
                        className="cursor-pointer"
                      >
                        <Checkbox
                          checked={selected}
                          aria-label={`Select ${displayUserName(user)}`}
                          className="pointer-events-none"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{displayUserName(user)}</p>
                          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                        </div>
                        {selected && <Check className="h-4 w-4 text-primary" />}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>

            <Textarea
              value={manualEmails}
              onChange={(event) => setManualEmails(event.target.value)}
              placeholder="Add email addresses..."
              className="min-h-[72px] resize-none"
            />
            <Textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Optional message..."
              className="min-h-[84px] resize-none"
              maxLength={2000}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:space-x-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleShare()}
            disabled={selectedUnits.length === 0 || recipientEmails.length === 0 || sharing}
            className="gap-2"
          >
            {sharing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Send {selectedUnits.length > 0 ? `${selectedUnits.length} ` : ''}Report
            {selectedUnits.length === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
